import { ActivityAthlete, Team, TeamsApi } from 'oas';
import { mockResponse, useApiQuery } from '../use-api-query';
import { UseQueryResult } from '@tanstack/react-query';
import { useApiQueries } from 'api/use-api-queries';
import { useMemo } from 'react';
import { useGetSetting } from 'api/settings';

/**
 * Get all account teams.
 * @param parameters Arguments forwarded to {@link TeamsApi.getAllTeams}.
 * @returns A list of all teams for the account.
 */
export const useGetTeams = (...parameters: Parameters<TeamsApi['getAllTeams']>): UseQueryResult<Team[]> => {
    return useApiQuery(
        ['teams', parameters],
        (apiConfig, axiosInstance) => new TeamsApi(apiConfig, '', axiosInstance).getAllTeams(...parameters),
        {
            staleTime: 3600000,
        },
    );
};

interface FilterOptions {
    includeArchive?: boolean;
    includeDemo?: boolean;
    excludeIds?: string[];
}

/**
 * Get teams with exclusions.
 * Defaults to excluding Archive and Demo teams.
 * @param includeArchive include team set as 'Archive' in settings
 * @param includeDemo include Demo Team
 * @param excludeIds custom teams to be removed from the list
 * @returns a list of filtered teams
 */
export const useGetFilteredTeams = (options: FilterOptions = {}) => {
    const { includeArchive = false, includeDemo = false, excludeIds = [] } = options;

    const { data: teams, isLoading: teamsLoading, ...rest } = useGetTeams();

    const archiveSetting = useGetSetting('ArchiveTeam');
    const archiveTeamId = archiveSetting?.value;

    const DEMO_TEAM_NAME = 'Demo Team';
    // avoid re-finding on every render
    const demoTeamId = useMemo(
        () => teams?.find((t) => t.name.toLowerCase() === DEMO_TEAM_NAME.toLowerCase())?.id,
        [teams],
    );

    const filteredTeams = useMemo(() => {
        if (teamsLoading || !teams) {
            return [];
        }

        const ignoreList = new Set(
            [...excludeIds, !includeArchive ? archiveTeamId : null, !includeDemo ? demoTeamId : null].filter(
                Boolean,
            ) as string[],
        );

        return teams.filter((t) => !ignoreList.has(t.id));
    }, [teamsLoading, teams, excludeIds, includeArchive, archiveTeamId, includeDemo, demoTeamId]);

    return { data: filteredTeams, isLoading: teamsLoading, ...rest };
};

export const useDefaultTeam = (settings?: 'settings'[]): Team | undefined => {
    const teams = useGetTeams(settings || []);

    return teams.data?.find((team) => team.is_default);
};

export const useGetTeam = (teamId?: string, includeSettings?: boolean): Team | undefined => {
    const teams = useGetTeams(includeSettings ? ['settings'] : []);

    if (!teamId) {
        return undefined;
    }

    return teams.data?.find((team) => team.id === teamId);
};

export const useTeamSeasonDates = (teamId: string): { key: string; value: number }[] => {
    const team = useGetTeam(teamId, true);

    // season dates are sorted by date they were entered
    // so we have to manually guarantee that years are in order
    const seasonData = useMemo(() => {
        const sortedSettings = team?.settings
            ?.filter((s) => s.key.includes('SeasonStart'))
            .sort((a, b) => {
                const yearA = parseInt(a.key.match(/^(\d{4})/)?.[1] || '0', 10);
                const yearB = parseInt(b.key.match(/^(\d{4})/)?.[1] || '0', 10);
                return yearB - yearA;
            });

        return sortedSettings || [];
    }, [team]);

    return seasonData;
};

export const useDefaultTeamSeasonDates = (): { key: string; value: number }[] => {
    const defaultTeam = useDefaultTeam();
    const seasonDates = useTeamSeasonDates(defaultTeam ? defaultTeam.id : '');

    return seasonDates;
};

export const useGetAthletesForTeam: (
    ...parameters: Parameters<TeamsApi['getAthletesForTeam']>
) => UseQueryResult<Array<ActivityAthlete>> = (id, options = {}) => {
    return useApiQuery(
        ['teams', id, 'athletes'],
        (apiConfig, axiosInstance) => {
            return id !== ''
                ? new TeamsApi(apiConfig, '', axiosInstance).getAthletesForTeam(id, options)
                : mockResponse([]);
        },
        {
            staleTime: 3600000,
        },
    );
};

export const useGetAthletesForMultipleTeams = (teamIds: string[], options = {}) => {
    const queryKeys = teamIds.map((id) => ['teams', id, 'athletes']);

    const results = useApiQueries<ActivityAthlete[], string>(
        queryKeys,
        teamIds,
        (apiConfig, axiosInstance, teamId) =>
            new TeamsApi(apiConfig, '', axiosInstance).getAthletesForTeam(teamId, options),
        options,
    );

    const isLoading = results.some((result) => result.isLoading);
    const error = results.find((result) => result.error)?.error;

    // results into a single object, mapping teamId to athletes data
    const data = results.reduce((acc, result, index) => {
        if (result.data) {
            acc[teamIds[index]] = result.data;
        }
        return acc;
    }, {} as Record<string, ActivityAthlete[]>);

    return { data, isLoading, error };
};

export const useGetAllAthletesForTeams = (
    teamIds: string[],
    options = {},
): { data: ActivityAthlete[]; isLoading: boolean; error?: unknown } => {
    const queryKeys = teamIds.map((id) => ['teams', id, 'athletes', 'flat']);

    const results = useApiQueries<ActivityAthlete[], string>(
        queryKeys,
        teamIds,
        (apiConfig, axiosInstance, teamId) =>
            new TeamsApi(apiConfig, '', axiosInstance).getAthletesForTeam(teamId, options),
        options,
    );

    const isLoading = results.some((result) => result.isLoading);
    const error = results.find((result) => result.error)?.error;

    // Flatten all athlete arrays
    const data: ActivityAthlete[] = results.flatMap((result) =>
        Array.isArray(result.data) ? result.data : result.data ? Object.values(result.data) : [],
    );

    return { data, isLoading, error };
};

export const useGetRelevantTeams = (): UseQueryResult<Team[]> => {
    return useApiQuery(
        ['teams', 'relevant'],
        (apiConfig, axiosInstance) =>
            new TeamsApi(apiConfig, '', axiosInstance).getAllTeams().then((response) => ({
                ...response,
                data: response.data.filter((team) => team.is_default || team.is_additional),
            })),
        {
            staleTime: 3000, // low stale time due to legacy settings not being able to invalidate query
        },
    );
};
